// apps/web/src/pages/payments/PaymentSuccess.tsx

import { useSearchParams, Link } from 'react-router-dom';
import { trpc } from '@/trpc/client';

import { Loader2, ShieldCheck, ShieldAlert, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthStore } from '@/store/authStore';

const PaymentSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const tx_ref = searchParams.get('tx_ref');
    const { user } = useAuthStore();
    const utils = trpc.useUtils();

    // Use the useQuery hook to poll for the payment status.
    const { data, isError, error } = trpc.payment.getPaymentStatus.useQuery(
        { tx_ref: tx_ref! },
        {
            enabled: !!tx_ref && !!user,
            // Refetch every 2 seconds until we get a result other than 'PENDING'
            refetchInterval: (data) => (data?.status === 'PENDING' ? 2000 : false),
            onSuccess: (data) => {
                if (data.status !== 'PENDING') {
                    // Once status is updated, invalidate dashboard data to ensure it's fresh
                    utils.job.getMyJobs.invalidate();
                }
            }
        }
    );

    // Determine the current state based on the polling query
    const isLoading = !data || data.status === 'PENDING';
    const isSuccess = data?.status === 'ESCROWED';
    const isFailed = isError || (data && data.status !== 'PENDING' && data.status !== 'ESCROWED');


    // Automatic redirect for mobile apps
    useEffect(() => {
        // Construct deep link with current query params (tx_ref)
        // Ensure we don't trigger this endlessly on web if it's not a PWA/TWA
        // But for this specific usecase, the user is coming from the payment gateway in an in-app browser or custom tab
        if (tx_ref) {
            const deepLink = `kclogistics://payment-success${window.location.search}`;
            console.log('Attempting auto-return to app:', deepLink);
            // Slight delay to allow page validtion/rendering
            const timer = setTimeout(() => {
                window.location.href = deepLink;
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [tx_ref]);

    if (!user) {
        // Construct deep link with current query params (tx_ref)
        const deepLink = `kclogistics://payment-success${window.location.search}`;

        return (
            <div className="container py-24 text-center animate-fade-in">
                <Card className="max-w-md mx-auto glass">
                    <CardHeader>
                        <CardTitle className="flex justify-center mb-4"><ShieldCheck className="w-12 h-12 text-primary" /></CardTitle>
                        <CardTitle>Payment Completed</CardTitle>
                        <CardDescription>
                            Tap below to return to the app and confirm your booking.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button asChild variant="premium" className="w-full">
                            <a href={deepLink}>Return to App</a>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!tx_ref) {
        // Handle case where user lands here without a transaction reference
        return <div className="container py-24 text-center"><Card className="max-w-md mx-auto"><CardHeader><CardTitle className="flex justify-center mb-4"><ShieldAlert className="w-12 h-12 text-destructive" /></CardTitle><CardTitle>Verification Error</CardTitle><CardDescription>Payment reference is missing.</CardDescription></CardHeader></Card></div>
    }

    return (
        <div className="container py-24 text-center animate-fade-in">
            <Card className="max-w-md mx-auto glass">
                <CardHeader>
                    {isLoading && <CardTitle className="flex justify-center mb-4"><Loader2 className="w-12 h-12 text-primary animate-spin" /></CardTitle>}
                    {isFailed && <CardTitle className="flex justify-center mb-4"><AlertTriangle className="w-12 h-12 text-destructive" /></CardTitle>}
                    {isSuccess && <CardTitle className="flex justify-center mb-4"><ShieldCheck className="w-12 h-12 text-accent" /></CardTitle>}

                    <CardTitle>
                        {isLoading && "Confirming Payment..."}
                        {isFailed && "Payment Failed"}
                        {isSuccess && "Payment Successful!"}
                    </CardTitle>
                    <CardDescription>
                        {isLoading && "Please wait while we confirm the transaction with the provider. This may take a moment."}
                        {isFailed && (error?.message || "An error occurred, or the payment was not completed successfully.")}
                        {isSuccess && `Your job is now funded and has been set to ACTIVE status. You will be notified when a transporter accepts it.`}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Button asChild variant="premium" disabled={isLoading}>
                        <Link to="/dashboard">Return to Dashboard</Link>
                    </Button>
                </CardContent>
            </Card>
        </div>
    )
};

export default PaymentSuccessPage;